import { contractTypesQueryOptions } from '@/shared/contract-types-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  revalidateLogic,
  useDebouncedValue,
  useForm,
  useFormErrors,
  useStore,
} from '@eureca/ui/forms';
import { utcIsoToZonedParts, type ComboboxOption } from '@eureca/ui';
import { z } from 'zod';
import { APP_TIMEZONE } from '@/lib/timezone';
import {
  consumePendingNameError,
  getPendingCoverFile,
  loadProgramDraft,
  saveProgramDraft,
  setPendingCoverFile,
} from '../program-create-draft';
import {
  ProgramCreatePage as ProgramCreatePageComponent,
  type ProgramCreateValues,
} from './ProgramCreatePage.component';
import { companySearchQueryOptions } from './company-search-query';
import { checkActiveProgramNameTaken } from './active-program-by-name-query';

const COMPANY_SEARCH_DEBOUNCE_MS = 300;
const DUPLICATE_PROGRAM_NAME_MESSAGE =
  'Esse nome já está em uso em um programa ativo. Crie um diferente, por favor!';

// `startDate`/`endDate` são instantes UTC (ISO 8601) editados em
// America/Sao_Paulo pelo DateTimePicker.
function isValidInstant(s: string): boolean {
  return !Number.isNaN(Date.parse(s));
}

/** Hoje (dia civil) em America/Sao_Paulo como `YYYY-MM-DD`. */
function todayBrt(): string {
  return utcIsoToZonedParts(new Date().toISOString(), APP_TIMEZONE).date;
}

/** Dia civil (America/Sao_Paulo) de um instante UTC, como `YYYY-MM-DD`. */
function brtDate(iso: string): string {
  return utcIsoToZonedParts(iso, APP_TIMEZONE).date;
}

// Cross-field guards live in a single superRefine so the schema has one
// extension point for future date/uniqueness checks (EU-150 RN-03, RN-05 +
// past-start). end > start is enforced strictly per RN-05 ("data posterior").
const schema = z
  .object({
    companyId: z.string().uuid('Selecione uma empresa'),
    // companyName is form-only state (display label) — no validation needed.
    companyName: z.string(),
    name: z.string().min(1, 'Informe o nome do programa'),
    description: z.string().min(1, 'Informe a descrição'),
    contractType: z.string().min(1, 'Selecione o tipo de contratação'),
    startDate: z
      .string()
      .min(1, 'Selecione uma data de início')
      .refine(isValidInstant, 'Selecione uma data válida'),
    endDate: z
      .string()
      .min(1, 'Selecione uma data de fim')
      .refine(isValidInstant, 'Selecione uma data válida'),
    targetRegistrations: z
      .string()
      .min(1, 'Informe a meta de inscritos')
      .regex(/^\d+$/, 'Informe um número inteiro'),
  })
  .superRefine((val, ctx) => {
    // Início não pode ser antes de HOJE (dia civil em Brasília).
    if (val.startDate && isValidInstant(val.startDate) && brtDate(val.startDate) < todayBrt()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Escolha uma data a partir de hoje',
      });
    }
    // Fim deve ser posterior ao início — comparação por INSTANTE.
    if (
      val.startDate &&
      val.endDate &&
      isValidInstant(val.startDate) &&
      isValidInstant(val.endDate) &&
      new Date(val.endDate).getTime() <= new Date(val.startDate).getTime()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Escolha uma data posterior ao início',
      });
    }
  });

const MAX_COVER_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_COVER_TYPES = ['image/jpeg', 'image/png'];
const COVER_SIZE_ERROR = 'O arquivo excede o limite de 5MB. Por favor, envie uma versão menor.';
const COVER_FORMAT_ERROR = 'Formato não aceito. Envie seu arquivo em JPG ou PNG.';
const COVER_RATIO_ERROR =
  'O arquivo não está na proporção correta. Por favor, envie com a proporção 16:9.';

/**
 * Reads the image's natural dimensions to enforce RN-04 (aspect 16:9). Pixel
 * dims are integers, so we cross-multiply (`w*9 === h*16`) instead of float
 * comparison — that matches "exact 16:9" without false negatives on rasters
 * that can't ever be exactly 1.7777… in float. Resolves to `null` if the
 * browser can't decode the file (corrupt / not really an image).
 */
function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims.width > 0 && dims.height > 0 ? dims : null);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

export default function ProgramCreatePage() {
  // EU-709 RN-01 — os tipos vêm do catálogo (origem única, espelhada com o
  // `Tipo de vaga`). Enquanto carrega, a lista fica vazia: melhor nenhum chip
  // do que quatro chips desatualizados escritos no cliente.
  const contractTypesQuery = useQuery(contractTypesQueryOptions());
  const contractTypeOptions = useMemo(
    () => (contractTypesQuery.data ?? []).map((t) => ({ value: t.key, label: t.label })),
    [contractTypesQuery.data]
  );

  const navigate = useNavigate();
  const { companyId: companyIdFromUrl } = useSearch({ from: '/programas/novo' });
  const [coverFile, setCoverFile] = useState<File | null>(() => getPendingCoverFile());
  const [coverError, setCoverError] = useState<string | null>(null);

  // Object URL for the preview. Revoked on unmount + on every change to avoid
  // leaking blob URLs across re-renders (Chrome happily keeps them around
  // until the document unloads otherwise).
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!coverFile) {
      setCoverPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(coverFile);
    setCoverPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [coverFile]);

  const [companyQuery, setCompanyQuery] = useState('');
  const debouncedCompanyQuery = useDebouncedValue(companyQuery, COMPANY_SEARCH_DEBOUNCE_MS);
  const companiesQuery = useQuery(companySearchQueryOptions(debouncedCompanyQuery));

  const [isCheckingName, setIsCheckingName] = useState(false);
  // EU-150 RN-03 — async server-side error for duplicate program name. Kept
  // outside the form's fieldMeta because TanStack's submit lifecycle clears
  // errors after onSubmit returns; carrying it as local state lets the next
  // change to `name` (see useEffect below) clear it on its own.
  const [duplicateNameError, setDuplicateNameError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      companyId: '',
      companyName: '',
      name: '',
      description: '',
      contractType: '',
      startDate: '',
      endDate: '',
      targetRegistrations: '',
    } satisfies ProgramCreateValues,
    // Validate when the field loses focus, then keep validating on every
    // change. This way the user sees errors as soon as they tab out of an
    // empty/invalid field — no need to wait for the submit click.
    validationLogic: revalidateLogic({ mode: 'blur', modeAfterSubmission: 'change' }),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      // RN-03 — block advance when an ACTIVE program in the same company
      // already owns this name. Pre-flight check rather than relying on Core
      // 409 so the error lands inline at Step 1, not at the final submit on
      // Step 3 (the UX the QA report expects).
      setIsCheckingName(true);
      let taken: boolean;
      try {
        taken = await checkActiveProgramNameTaken({
          name: value.name,
          companyId: value.companyId,
        });
      } finally {
        setIsCheckingName(false);
      }
      if (taken) {
        setDuplicateNameError(DUPLICATE_PROGRAM_NAME_MESSAGE);
        return;
      }
      setDuplicateNameError(null);
      saveProgramDraft({
        companyId: value.companyId,
        companyName: value.companyName,
        name: value.name,
        description: value.description,
        contractType: value.contractType,
        startDate: value.startDate,
        endDate: value.endDate,
        targetRegistrations: value.targetRegistrations,
      });
      await navigate({ to: '/programas/novo/beneficios' });
    },
  });

  // Restore form from draft (and the URL companyId hint) on mount.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const draft = loadProgramDraft();
    const initialCompanyId = draft.companyId ?? companyIdFromUrl ?? '';
    if (initialCompanyId) form.setFieldValue('companyId', initialCompanyId);
    if (draft.companyName) form.setFieldValue('companyName', draft.companyName);
    if (draft.name) form.setFieldValue('name', draft.name);
    if (draft.description) form.setFieldValue('description', draft.description);
    if (draft.contractType) form.setFieldValue('contractType', draft.contractType);
    if (draft.startDate) form.setFieldValue('startDate', draft.startDate);
    if (draft.endDate) form.setFieldValue('endDate', draft.endDate);
    if (draft.targetRegistrations)
      form.setFieldValue('targetRegistrations', draft.targetRegistrations);
    // RN-03 hand-off — if the Step 3 submit hit the BFF dup gate and routed
    // back here, replay the inline error next to the name field so the
    // consultor sees the same UX as the pre-flight at this step.
    const pendingDupError = consumePendingNameError();
    if (pendingDupError) setDuplicateNameError(pendingDupError);
  }, []);

  const values = useStore(form.store, (state) => state.values);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const fieldErrors = useFormErrors(form);
  const errors: Record<string, string> = { ...fieldErrors };
  if (coverError) errors.cover = coverError;
  // Surface RN-03 dup error in the same slot as zod's `name` error, but only
  // when zod isn't already complaining about the field (zod errors win).
  if (duplicateNameError && !errors.name) errors.name = duplicateNameError;

  const companyOptions = useMemo<ComboboxOption[]>(
    () =>
      (companiesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [companiesQuery.data]
  );

  async function handleCoverChange(file: File | null) {
    if (!file) {
      setCoverFile(null);
      setPendingCoverFile(null);
      setCoverError(null);
      return;
    }
    // RN-04 — format first (cheapest check), then size, then 16:9.
    if (!ALLOWED_COVER_TYPES.includes(file.type)) {
      setCoverError(COVER_FORMAT_ERROR);
      setCoverFile(null);
      setPendingCoverFile(null);
      return;
    }
    if (file.size > MAX_COVER_SIZE_BYTES) {
      setCoverError(COVER_SIZE_ERROR);
      setCoverFile(null);
      setPendingCoverFile(null);
      return;
    }
    const dims = await readImageDimensions(file);
    if (!dims || dims.width * 9 !== dims.height * 16) {
      setCoverError(COVER_RATIO_ERROR);
      setCoverFile(null);
      setPendingCoverFile(null);
      return;
    }
    setCoverError(null);
    setCoverFile(file);
    setPendingCoverFile(file);
  }

  function handleCompanyChange(id: string, name: string) {
    form.setFieldValue('companyId', id);
    form.setFieldValue('companyName', name);
    saveProgramDraft({ companyId: id, companyName: name });
  }

  function handleSubmit() {
    void form.handleSubmit();
  }

  return (
    <ProgramCreatePageComponent
      values={values}
      errors={errors}
      coverFileName={coverFile?.name ?? null}
      coverPreviewUrl={coverPreviewUrl}
      isSubmitting={isSubmitting || isCheckingName}
      progress={30}
      companyOptions={companyOptions}
      contractTypeOptions={contractTypeOptions}
      isCompaniesLoading={companiesQuery.isFetching}
      onCompanySearch={setCompanyQuery}
      onCompanyChange={handleCompanyChange}
      onFieldChange={(field, value) => {
        if (field === 'name' && duplicateNameError) setDuplicateNameError(null);
        form.setFieldValue(field, value);
      }}
      onCoverChange={handleCoverChange}
      onSubmit={handleSubmit}
    />
  );
}
